export type ArchiveEntry = {
  id: string;
  category: string;
  title: string;
  excerpt: string;
  date: string;
  batch: string;
  records: number;
  tags: string[];
  image: string;
  featured?: boolean;
};

export const navLinks = [
  { label: "存档总览", href: "#archive" },
  { label: "资源入口", href: "#portal" },
  { label: "留档时间线", href: "#timeline" },
  { label: "备份承诺", href: "#trust" },
  { label: "常见问题", href: "#faq" },
];

export const heroStats = [
  { value: "1,280+", label: "存档批次（组）" },
  { value: "46.8 万", label: "留档条目" },
  { value: "每周 1 次", label: "完整性校验" },
  { value: "2026-09-12", label: "最近更新" },
];

export const entryPortals = [
  {
    id: "portal-time",
    icon: "ri-calendar-check-line",
    keyword: "按时间检索",
    title: "以日期与批次号定位存档",
    desc: "输入大致的时间范围或批次编号，即可定位到对应的留档记录，回看任意时间节点的存证与底稿。",
    meta: "覆盖 2025-12 至今全部批次",
    image:
      "https://readdy.ai/api/search-image?query=stack%20of%20aged%20archival%20ledgers%20with%20date%20stamps%2C%20warm%20amber%20reading%20lamp%20light%2C%20dark%20charcoal%20background%2C%20cinematic%20moody%20still%20life%2C%20no%20people%2C%20soft%20shadows%2C%20high%20detail&width=900&height=640&seq=hl-portal-time-01&orientation=landscape",
  },
  {
    id: "portal-person",
    icon: "ri-search-eye-line",
    keyword: "按人物检索",
    title: "依据条目索引集中回看",
    desc: "通过条目索引定位相关留档，把同一话题下的过往存证、底稿与版本变更集中到一起，脉络更清楚。",
    meta: "条目索引每日同步更新",
    image:
      "https://readdy.ai/api/search-image?query=rows%20of%20vintage%20index%20card%20catalog%20drawers%20in%20a%20dark%20archive%20room%2C%20warm%20amber%20accent%20light%2C%20cinematic%20moody%20atmosphere%2C%20no%20people%2C%20deep%20shadows%2C%20high%20detail&width=900&height=640&seq=hl-portal-person-01&orientation=landscape",
  },
  {
    id: "portal-topic",
    icon: "ri-folder-shield-2-line",
    keyword: "专题归档",
    title: "按主题聚合的完整备份",
    desc: "同一主题的存档成套留存，图文、底稿与时间线一并归档，重要内容成套备份，回看时不会遗漏。",
    meta: "已完成 68 个专题整编",
    image:
      "https://readdy.ai/api/search-image?query=neatly%20organised%20archive%20folder%20stacks%20with%20labels%20on%20a%20dark%20desk%2C%20warm%20amber%20spotlight%2C%20cinematic%20moody%20still%20life%2C%20no%20people%2C%20minimal%20composition%2C%20high%20detail&width=900&height=640&seq=hl-portal-topic-01&orientation=landscape",
  },
];

export const archiveCategories = ["全部", "话题存档", "底稿备份", "时间线梳理", "声明留存"];

export const archiveEntries: ArchiveEntry[] = [
  {
    id: "hl-2026-0421",
    category: "话题存档",
    title: "公共话题留档 · 完整批次存档",
    excerpt: "围绕同一话题的图文存证与底稿集中归档，包含原始来源标注与版本说明，便于随时回看核对。",
    date: "2026-08-21",
    batch: "HL-2026-0421",
    records: 128,
    tags: ["图文存证", "底稿齐全"],
    image:
      "https://readdy.ai/api/search-image?query=stacked%20paper%20archive%20folders%20on%20a%20dark%20wooden%20table%2C%20warm%20amber%20light%20from%20the%20side%2C%20cinematic%20moody%20still%20life%2C%20no%20people%2C%20clean%20minimal%20composition%2C%20high%20detail&width=1000&height=720&seq=hl-card-01&orientation=landscape",
    featured: true,
  },
  {
    id: "hl-2026-0398",
    category: "底稿备份",
    title: "原始底稿归档 · 第 042 号备份",
    excerpt: "保留原始底稿与修改前后版本，标注写入时间与批次编号，重要内容多处备份，不怕丢失。",
    date: "2026-07-30",
    batch: "HL-2026-0398",
    records: 96,
    tags: ["原始底稿", "版本留存"],
    image:
      "https://readdy.ai/api/search-image?query=old%20printed%20documents%20and%20newspaper%20clippings%20flat%20lay%20on%20dark%20surface%2C%20warm%20moody%20amber%20lighting%2C%20cinematic%20atmosphere%2C%20no%20people%2C%20soft%20shadows%2C%20high%20detail&width=1000&height=720&seq=hl-card-02&orientation=landscape",
    featured: true,
  },
  {
    id: "hl-2026-0356",
    category: "时间线梳理",
    title: "话题时间线 · 梳理留档版",
    excerpt: "按时间顺序梳理关键节点，每个节点对应可核对的留档记录与来源说明，脉络一目了然。",
    date: "2026-07-12",
    batch: "HL-2026-0356",
    records: 74,
    tags: ["时间线", "节点标注"],
    image:
      "https://readdy.ai/api/search-image?query=dark%20filing%20cabinet%20drawers%20slightly%20open%20in%20an%20archive%20room%2C%20warm%20amber%20rim%20light%2C%20cinematic%20moody%20still%20life%2C%20no%20people%2C%20deep%20shadows%2C%20high%20detail&width=1000&height=720&seq=hl-card-03&orientation=landscape",
  },
  {
    id: "hl-2026-0321",
    category: "声明留存",
    title: "多方声明对照 · 留存备份",
    excerpt: "相关声明原文截图与文字版本对照留存，注明获取时间与批次号，方便日后逐条核对。",
    date: "2026-06-28",
    batch: "HL-2026-0321",
    records: 63,
    tags: ["声明对照", "截图留存"],
    image:
      "https://readdy.ai/api/search-image?query=magnifying%20glass%20resting%20over%20printed%20documents%20on%20dark%20desk%2C%20warm%20amber%20lighting%2C%20cinematic%20moody%20atmosphere%2C%20no%20people%2C%20soft%20shadows%2C%20high%20detail&width=1000&height=720&seq=hl-card-04&orientation=landscape",
  },
  {
    id: "hl-2026-0287",
    category: "话题存档",
    title: "图文对照存证 · 完整留档",
    excerpt: "图片与文字证据成对归档，保留原始文件信息与来源说明，形成可回看的完整存证记录。",
    date: "2026-06-05",
    batch: "HL-2026-0287",
    records: 152,
    tags: ["图文对照", "完整版"],
    image:
      "https://readdy.ai/api/search-image?query=paper%20notes%20pinned%20on%20a%20dark%20board%20forming%20a%20timeline%2C%20warm%20amber%20accent%20light%2C%20cinematic%20moody%20still%20life%2C%20no%20people%2C%20minimal%20composition%2C%20high%20detail&width=1000&height=720&seq=hl-card-05&orientation=landscape",
  },
  {
    id: "hl-2026-0254",
    category: "底稿备份",
    title: "历史版本回溯 · 变更记录归档",
    excerpt: "记录每次内容变更的时间与说明，保留历史版本快照，需要时可回溯到任意一次留档状态。",
    date: "2026-05-19",
    batch: "HL-2026-0254",
    records: 88,
    tags: ["版本回溯", "变更记录"],
    image:
      "https://readdy.ai/api/search-image?query=sealed%20kraft%20paper%20envelope%20on%20a%20dark%20desk%20under%20warm%20spotlight%2C%20cinematic%20moody%20still%20life%2C%20no%20people%2C%20soft%20shadows%2C%20minimal%20composition%2C%20high%20detail&width=1000&height=720&seq=hl-card-06&orientation=landscape",
  },
];

export const timelineMilestones = [
  {
    date: "2026-09",
    title: "存档库完成第 3 次完整性校验",
    desc: "全量批次逐条比对原始底稿，校验结果与更新说明一并留档。",
  },
  {
    date: "2026-07",
    title: "新增原始底稿比对与版本回溯",
    desc: "同一存档的历次版本可并列查看，变更点标注到具体条目。",
  },
  {
    date: "2026-05",
    title: "留档批次突破 300 组",
    desc: "专题归档整编完成，重要内容成套备份并统一编号。",
  },
  {
    date: "2026-03",
    title: "按人物检索索引上线",
    desc: "条目索引与批次号打通，可按索引集中回看相关留档。",
  },
  {
    date: "2025-12",
    title: "黑料看料留档档案库正式对外开放",
    desc: "首批历史存档完成归档，确立批次号 + 时间双重标注规则。",
  },
];

export const howItWorks = [
  {
    step: "01",
    icon: "ri-search-line",
    title: "确定检索方式",
    desc: "按时间、批次号或条目索引任选一种方式进入，先缩小回看范围。",
  },
  {
    step: "02",
    icon: "ri-file-list-3-line",
    title: "核对留档信息",
    desc: "查看批次号、更新时间与来源标注，确认这份存证是否为目标记录。",
  },
  {
    step: "03",
    icon: "ri-history-line",
    title: "回看与比对",
    desc: "在图文、底稿与历史版本之间切换，随时复看，内容不会因时间久远而丢失。",
  },
];

export const trustPromises = [
  {
    icon: "ri-archive-2-line",
    title: "完整备份",
    desc: "每一组存档都保留原始底稿与图文版本，重要内容成套留存，不做删减。",
  },
  {
    icon: "ri-fingerprint-line",
    title: "可追溯",
    desc: "批次号与更新时间双重标注，来源与变更说明齐全，随时核对留档出处。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续更新",
    desc: "新记录按批次归档，旧记录保留历史版本，留档连续、完整、可长期回看。",
  },
];

export const trustStats = [
  { value: "99.6%", label: "批次可用率" },
  { value: "3 份", label: "异地冗余备份" },
  { value: "7 年", label: "历史留档跨度" },
];

export const faqItems = [
  {
    q: "首页的「存档」和「留档」有什么区别？",
    a: "存档偏向按批次集中归档的完整内容，留档侧重对单条记录的持续保存与更新说明。两者互为补充：先存档，再留档，最终形成可长期回看的备份记录。",
  },
  {
    q: "按时间和按人物检索，结果会不一样吗？",
    a: "入口不同，指向的都是同一套留档数据。按时间适合回看某个阶段的整体情况，按人物适合集中核对同一索引下的过往存证，结果可以相互印证。",
  },
  {
    q: "留档的记录会一直保存吗？",
    a: "重要内容会保持多份冗余备份并定期校验完整性。已归档的批次不会随意下架，历史版本也会作为备份保留。",
  },
  {
    q: "底稿备份里都包含哪些内容？",
    a: "包含原始底稿、修改前后版本以及写入时间标注。每次内容变更都会生成一条变更记录，回看时可以清楚看到改了什么、什么时候改的。",
  },
  {
    q: "怎么核对某个存档批次的更新时间？",
    a: "每个批次都带有唯一的批次号与更新时间，可在存档总览中按批次号定位，查看它的留档说明与最近一次校验结果。",
  },
  {
    q: "找不到想要的历史记录怎么办？",
    a: "可以先用关键词或条目索引做一次范围检索，再结合时间线缩小范围。若仍无法定位，说明该内容可能尚未归档，后续会按批次持续补充。",
  },
];

export const footerColumns = [
  {
    title: "关于存档",
    links: ["留档档案库简介", "批次编号规则", "备份与校验说明", "更新日志"],
  },
  {
    title: "检索入口",
    links: ["按时间检索", "按人物检索", "专题归档", "关键词检索"],
  },
  {
    title: "留档规则",
    links: ["原始底稿留存", "版本回溯说明", "来源标注规范", "完整性校验周期"],
  },
];

export const tickerItems = [
  "存档批次 HL-2026-0421 已完成校验",
  "新增原始底稿备份 96 条",
  "专题归档「完整备份」整编完成",
  "留档时间线新增 5 个节点",
  "按人物检索索引已同步更新",
  "第 3 次完整性校验通过率 99.6%",
];